import React, {useState} from "react";
import {Checkbox, Collapse, IconButton, Radio, TableCell, TableRow, Tooltip} from "@mui/material";

// icons
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CircularProgress from '@mui/material/CircularProgress';
import SourceIcon from "@mui/icons-material/Source";
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import {HeadCell} from "./types";
import CircleIcon from '@mui/icons-material/Circle';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import UpdateDisabledIcon from '@mui/icons-material/UpdateDisabled';
import FlareIcon from '@mui/icons-material/Flare';
import {OverridableStringUnion} from "@mui/types";
import { REQUEST_STATUS } from "../../../constants/_pickerValues";


interface IRow {
    row: any;
    headCells: HeadCell[];
    selectable?: string;
    canEdit?: boolean;
    canView?: boolean;
    loadingIcon?: boolean;
    canRun?: boolean;
    canDelete?: boolean;
    canViewSource?: boolean;
    canExpand?: boolean;
    canApprove?: boolean;
    canRenew?: boolean;
    canRetry?: boolean;
    canSkip?: boolean;
    isItemSelected?: boolean;
    handleClick?: (event: React.MouseEvent<unknown>, name: string) => void;
    handleExpand?: (row: any) => void;
    handleView?: (row: any, event: React.MouseEvent<HTMLButtonElement>) => void;
    handleViewSource?: (row: any, event: React.MouseEvent<HTMLButtonElement>) => void;
    handleEdit?: (row: any, event: React.MouseEvent<HTMLButtonElement>) => void;
    handleDelete?: (row: any) => void;
    handleRun?: (row: any) => void;
    handleApprove?: (row: any) => void;
    handleReject?: (row: any) => void;
    handleRenew?: (row: any) => void;
    handleRetry?: (row: any) => void;
    handleSkip?: (row: any) => void;
    children?: React.ReactNode;
    deletedRows?: any[];
    deletedIdLoading?: any;
    deleteId?: any;
    disabledButton?: boolean;
}

const Row = (props: IRow) => {
    const idpUsersGroup = "idp users production";

    const {
        row, headCells, selectable, canEdit, canView, canViewSource, canDelete, canRun, canExpand, isItemSelected, deletedRows,
        handleClick, handleExpand, handleView, handleEdit, handleDelete, handleRun, loadingIcon, handleViewSource, deletedIdLoading, deleteId,
        children, handleApprove, canApprove, handleReject, canRenew, handleRenew, canRetry, handleRetry, canSkip, handleSkip, disabledButton
    } = props;

    const numActions = [canView, canEdit, canDelete, canRun, canViewSource, canExpand, canApprove, canRetry, canSkip].filter(Boolean).length;
    const maxActionsWidth = Math.min(32 + numActions*34, 175);

    const [open, setOpen] = useState(false);

    const handleRowExpand = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: any) => {

        event.preventDefault();
        event.stopPropagation();

        if (handleExpand) {
            handleExpand(row)
        }
        setOpen(!open);
    }


    const handleRowView = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: any) => {
        event.preventDefault();
        event.stopPropagation();

        if (handleView) {
            handleView(row, event);
        }
    }

    const handleRowSource = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: any) => {
        event.preventDefault();
        event.stopPropagation();

        if (handleViewSource) {
            handleViewSource(row, event);
        }
    }

    const handleRowEdit = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: any) => {
        event.preventDefault();
        event.stopPropagation();

        if (handleEdit) {
            handleEdit(row, event);
        }

    }

    const handleRowRun = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: any) => {
        event.preventDefault();
        event.stopPropagation();

        if (handleRun) {
            handleRun(row);
        }

    }

    const handleRowDelete = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: any) => {
        event.preventDefault();
        event.stopPropagation();

        if (handleDelete) {
            handleDelete(row);
        }
    }

    const handleRowApprove = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: any) => {
        event.preventDefault();
        event.stopPropagation();

        if (handleApprove) {
            handleApprove(row);
        }
    }

    const handleRowReject = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: any) => {
        event.preventDefault();
        event.stopPropagation();

        if (handleReject) {
            handleReject(row);
        }
    }

    const handleRowRenew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: any) => {
        event.preventDefault();
        event.stopPropagation();

        if (handleRenew) {
            handleRenew(row);
        }
    }

    const handleRowRetry = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: any) => {
        event.preventDefault();
        event.stopPropagation();

        if (handleRetry) {
            handleRetry(row);
        }
    }

    const handleRowSkip = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: any) => {
        event.preventDefault();
        event.stopPropagation();

        if (handleSkip) {
            handleSkip(row);
        }
    }

    return (
        <React.Fragment>
            <TableRow
                sx={{ '& > *': { borderBottom: '1px solid rgba(224, 224, 224, 1)' } }}
                hover
                key={row.name}
                onClick={handleClick ? (event) => handleClick(event, row.name) : undefined}
                role="checkbox"
                tabIndex={-1}
                selected={isItemSelected}
            >
                {selectable === 'multiple' &&
                    <TableCell padding="checkbox">
                        <Checkbox
                            color="primary"
                            checked={isItemSelected}
                        />
                    </TableCell>}
                {selectable === 'single' &&
                    <TableCell padding="checkbox">
                        <Radio
                            color="primary"
                            checked={isItemSelected}
                        />
                    </TableCell>}
                {selectable === 'na' &&
                    <TableCell padding="checkbox">

                    </TableCell>}

                {
                    headCells.map((headCell) => {
                        if (headCell.id === 'selected') {
                            return null
                        }
                        let data = 'primary';
                        if (deletedRows !== undefined) {
                            if (deletedIdLoading?.includes(row.overrideId) && deleteId) {
                                data = 'lightgrey';
                            }
                            else if (deletedIdLoading?.includes(row.batchCode) && deleteId) {
                                data = 'lightgrey';
                            }
                            else if (deletedIdLoading?.includes(row.sourceId) && deleteId) {
                                data = 'lightgrey';
                            }
                            else if (deletedIdLoading?.includes(row.loaderBatchCode) && deleteId) {
                                data = 'lightgrey';
                            }
                            else if (deletedIdLoading?.includes(row.spokeId) && deleteId) {
                                data = 'lightgrey';
                            }
                            else if (deletedIdLoading?.includes(row.targetId) && deleteId) {
                                data = 'lightgrey';
                            }
                            else if (deletedIdLoading?.includes(row.id) && deleteId) {
                                data = 'lightgrey';
                            }
                            else {
                                data = 'primary';
                            }
                        }
                        let rowValue = row[headCell.id] as string
                        if (headCell.id === 'expired') {
                            if (rowValue === 'new') {
                                return (<TableCell key={headCell.id} width={headCell.width} align={'center'}>
                                    <Tooltip title="New">
                                        <FlareIcon color="action" />
                                    </Tooltip>
                                </TableCell>)
                            } else {
                                let expiredValue = rowValue as OverridableStringUnion<'error' | 'warning' | 'success' | 'disabled'>
                                let titleMap = {
                                    error: "Expired",
                                    warning: "Expiring Soon",
                                    success: "Valid",
                                    disabled: "Disabled"
                                }
                                return (<TableCell key={headCell.id} width={headCell.width} align={'center'}>
                                            <Tooltip title={titleMap[expiredValue]}>
                                                <CircleIcon color={expiredValue} fontSize="small" />
                                            </Tooltip>
                                        </TableCell>)
                            }
                        } if (headCell.id === 'inactive') {
                            return (<TableCell key={headCell.id} width={headCell.width} align={'center'}>
                                {
                                    rowValue === 'disabled' ?
                                        <Tooltip title="Inactive">
                                            <WarningAmberRoundedIcon fontSize="medium" />
                                        </Tooltip>
                                        : null
                                }</TableCell>)
                        } if (headCell.id === 'treatmentLevel') {
                            return (<TableCell key={headCell.id} width={headCell.width} align={'center'}>
                                {
                                    rowValue === 'medium' ?
                                        <Tooltip title="Medium Treatment">
                                            <SecurityIcon color="warning" />
                                        </Tooltip>
                                        : rowValue === 'high' ?
                                            <Tooltip title="High Treatment">
                                                <SecurityIcon color="error" />
                                            </Tooltip>
                                            : rowValue === 'very-high' ?
                                                <Tooltip title="Very High Treatment">
                                                    <SecurityIcon />
                                                </Tooltip>
                                                : rowValue === 'access' ?
                                                    <Tooltip title="Access">
                                                        <LockIcon />
                                                    </Tooltip>
                                                    : null

                                }</TableCell>)
                        } else if (headCell.id === 'accessControl') {
                            return (<TableCell key={headCell.id} width={headCell.width} align={'center'}>
                                {
                                    rowValue?.toLowerCase() !== idpUsersGroup ?
                                        <Tooltip title="This table is restricted">
                                            <LockIcon />
                                        </Tooltip>
                                        : null


                                }</TableCell>)
                        } else if (headCell.id === 'businessReason') {
                            return (<TableCell style={{ color: data, textOverflow: "ellipsis", wordBreak: "break-word" }}
                                               key={headCell.id}
                                               width={headCell.width} align={headCell.numeric ? 'right' : 'left'}>
                                        {
                                            rowValue ? <Tooltip title={rowValue} placement={"top"}>
                                                <IconButton
                                                    size="small"
                                                >
                                                    <b>...</b>
                                                </IconButton>
                                            </Tooltip> : null
                                        }
                            </TableCell>)
                        } else {
                            return (<TableCell style={{ color: data, textOverflow: "ellipsis", wordBreak: "break-word" }} key={headCell.id} width={headCell.width} align={headCell.numeric ? 'right' : 'left'}>
                                {
                                    //rowValue?.length > 30 ? rowValue?.substring(0, 3).concat('...') + rowValue?.substring(rowValue.length - 20) : rowValue
                                    rowValue
                                }</TableCell>)
                        }

                    })
                }
                {(canView || canEdit || canDelete || canRun || canViewSource || canExpand) &&
                    <TableCell width={maxActionsWidth + "px"} align="right">
                        {loadingIcon && deletedIdLoading?.includes(row.targetId) && <>
                            <CircularProgress size="1rem" /> deleting ...</>}
                        {loadingIcon && deletedIdLoading?.includes(row.sourceId) && <>
                            <CircularProgress size="1rem" /> deleting ...</>}
                        {loadingIcon && deletedIdLoading?.includes(row.batchCode) && <>
                            <CircularProgress size="1rem" /> deleting ...</>}
                        {loadingIcon && deletedIdLoading?.includes(row.loaderBatchCode) && <>
                            <CircularProgress size="1rem" /> deleting ...</>}
                        {loadingIcon && deletedIdLoading?.includes(row.overrideId) && <>
                            <CircularProgress size="1rem" /> deleting ...</>}
                        {loadingIcon && deletedIdLoading?.includes(row.requestId) && <>
                            <CircularProgress size="1rem" /> deleting ...</>}
                        {loadingIcon && deletedIdLoading?.includes(row.spokeId) && <>
                            <CircularProgress size="1rem" /> deleting ...</>}
                        {loadingIcon && deletedIdLoading?.includes(row.id) && <>
                            <CircularProgress size="1rem" /> deleting ...</>}
                        {canExpand &&
                            <IconButton
                                aria-label="expand row"
                                size="small"
                                onClick={(event) => handleRowExpand(event, row)}
                            >

                                {open ? <KeyboardArrowUpIcon color="action" /> : <KeyboardArrowDownIcon color="action" />}
                            </IconButton>
                        }
                        {canApprove && (row.status === REQUEST_STATUS.awaiting_review || row.status === REQUEST_STATUS.awaiting_approval) &&
                            <IconButton
                                size="small"
                                onClick={(event) => handleRowApprove(event, row)}
                                disabled={disabledButton}
                            >
                                <Tooltip title="Approve">
                                    <CheckCircleOutlineIcon color={disabledButton? "disabled" : "action"} />
                                </Tooltip>
                            </IconButton>}
                        {canApprove && (row.status === REQUEST_STATUS.awaiting_review || row.status === REQUEST_STATUS.awaiting_approval) &&
                            <IconButton
                                size="small"
                                onClick={(event) => handleRowReject(event, row)}
                                disabled={disabledButton}
                            >
                                <Tooltip title="Reject">
                                    <NotInterestedIcon color={disabledButton? "disabled" : "action"} />
                                </Tooltip>
                            </IconButton>}
                        {canRetry &&
                            <IconButton
                                size="small"
                                onClick={(event) => handleRowRetry(event, row)}
                                disabled={disabledButton}
                            >
                                <Tooltip title="Resolve Automation Error">
                                    <SyncProblemIcon color={disabledButton? "disabled" : "action"} />
                                </Tooltip>
                            </IconButton>}
                        {canSkip &&
                            <IconButton
                                size="small"
                                onClick={(event) => handleRowSkip(event, row)}
                                disabled={disabledButton}
                            >
                                <Tooltip title="Skip Automation Step">
                                    <UpdateDisabledIcon color={disabledButton? "disabled" : "action"} />
                                </Tooltip>
                            </IconButton>}
                        {canRenew &&
                            <IconButton
                                size="small"
                                onClick={(event) => handleRowRenew(event, row)}
                                disabled={disabledButton}
                            >
                                <Tooltip title="Renew">
                                    <MoreTimeIcon color={disabledButton? "disabled" : "action"} />
                                </Tooltip>
                            </IconButton>}
                        {canView &&
                            <IconButton
                                size="small"
                                onClick={(event) => handleRowView(event, row)}
                            >
                                <Tooltip title="View">
                                    <VisibilityIcon color="action" />
                                </Tooltip>
                            </IconButton>}
                        {canEdit &&
                            <IconButton
                                size="small"
                                onClick={(event) => handleRowEdit(event, row)}
                            >
                                <Tooltip title="Edit">
                                    <EditIcon color="action" />
                                </Tooltip>
                            </IconButton>}
                        {canDelete &&
                            <IconButton
                                size="small"
                                onClick={(event) => handleRowDelete(event, row)}
                                disabled={disabledButton}
                            >
                                <Tooltip title="Delete">
                                    <DeleteForeverIcon color={disabledButton? "disabled" : "action"} />
                                </Tooltip>
                            </IconButton>}
                        {canRun &&
                            <IconButton
                                size="small"
                                onClick={(event) => handleRowRun(event, row)}
                            >
                                <Tooltip title="Run">
                                    <AutorenewIcon color="action" />
                                </Tooltip>
                            </IconButton>}
                        {canViewSource &&
                            <IconButton
                                size="small"
                                onClick={(event) => handleRowSource(event, row)}
                            >
                                <Tooltip title="View Source">
                                    <SourceIcon color="action" />
                                </Tooltip>
                            </IconButton>}
                    </TableCell>
                }
            </TableRow>
            {canExpand &&
                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0, background: "#f5f5f5" }} colSpan={12}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            {children}
                        </Collapse>
                    </TableCell>
                </TableRow>
            }
        </React.Fragment>
    );
};

export default Row;